import React, { useState, FormEvent, useEffect } from 'react';
import { Suggest, ItemRenderer, ItemPredicate } from '@blueprintjs/select';
import { FormGroup, InputGroup, AnchorButton, Button, Checkbox, HTMLTable, MenuItem, Spinner } from "@blueprintjs/core";
import { AxiosInstance } from 'axios';
import { useTranslation } from 'react-i18next';

import { GlList, GlListItem, VoucherItem, Environment, Account } from './Types'
import * as utils from "./utils";

interface ActivityReportProps {
    svc: AxiosInstance,
    currentEnv?: Environment,
}

function ActivityReport({ svc, currentEnv = {} as Environment }: ActivityReportProps) {
    const { t, i18n } = useTranslation();
    const [isLoading, setIsLoading] = useState(false)
    const [data, setData] = useState({} as GlList)
    const [totals, setTotals] = useState([0,0])
    const [acList, setAcList] = useState([] as Account[])

    let date = new Date(), y = date.getFullYear(), m = date.getMonth()
    const [fromAcCode, setFromAcCode] = useState("")
    const [toAcCode, setToAcCode] = useState("")
    const [from, setFrom] = useState(utils.formatDate(new Date(y, m, 1)))
    const [to, setTo] = useState(utils.formatDate(date)) // new Date(y, m+1, 0)
    const [unposted, setUnposted] = useState(true)

    useEffect(() => {
        (async () => {
            if ("envId" in currentEnv) {
                try {
                    const res = await svc.post('/accountlist',
                        {
                            by: "acCode",
                            start: "",
                            limit: 0,
                            backward: false,
                            filters: {
                                isDetailOnly: true,
                            }
                        });

                    setAcList([{acCode: "", acName1:"Any", acName2:""} as Account].concat(res.data.data))
                    //setAcList(res.data.data)
                } catch (error) {
                    utils.showError(error)
                }
            }
        })()
    }, [svc, currentEnv])
    
    const acRenderer: ItemRenderer<Account> = (ac, { handleClick, modifiers, query }) => {
        if (!modifiers.matchesPredicate) {
            return null;
        }
        //const text = `${ac.acCode} - ${ac.acName1}`;
        const text = `${ac.acCode} ${ac.acName1.length > 0 || ac.acName2.length > 0 ? '-' : ''} ${ac.acName1+ac.acName2}`;
        return (
            <MenuItem
                active={modifiers.active}
                disabled={modifiers.disabled}
                label={ac.acCode}
                key={ac.acCode}
                onClick={handleClick}
                text={highlightText(text, query)}
            />
        );
    };

    const acPredicate: ItemPredicate<Account> = (query, ac, _index, exactMatch) => {
        const normalizedText = `${ac.acCode} - ${ac.acName1+ac.acName2}`.toLowerCase();
        const normalizedQuery = query.toLowerCase();

        if (exactMatch) {
            return normalizedText === normalizedQuery;
        } else {
            return normalizedText.indexOf(normalizedQuery) >= 0;
        }
    };

    const acValueRenderer = (ac: Account) => {
        return ac.acCode
    }

    function highlightText(text: string, query: string) {
        let lastIndex = 0;
        const words = query
            .split(/\s+/)
            .filter(word => word.length > 0)
            .map(escapeRegExpChars);
        if (words.length === 0) {
            return [text];
        }
        const regexp = new RegExp(words.join("|"), "gi");
        const tokens: React.ReactNode[] = [];
        while (true) {
            const match = regexp.exec(text);
            if (!match) {
                break;
            }
            const length = match[0].length;
            const before = text.slice(lastIndex, regexp.lastIndex - length);
            if (before.length > 0) {
                tokens.push(before);
            }
            lastIndex = regexp.lastIndex;
            tokens.push(<strong key={lastIndex}>{match[0]}</strong>);
        }
        const rest = text.slice(lastIndex);
        if (rest.length > 0) {
            tokens.push(rest);
        }
        return tokens;
    }

    function escapeRegExpChars(text: string) {
        //return text.replace(/([.*+?^=!:${}()|\[\]\/\\])/g, "\\$1");
        return text.replace(/([.*+?^=!:${}()|\\])/g, "\\$1");
    }

    const onSubmit = async (e: FormEvent) => {
        e.preventDefault()
        try {
            setIsLoading(true)
            const res = await svc.post('/gllist',
                {
                    fromAcCode: fromAcCode,
                    toAcCode: toAcCode,
                    fromDate: from,
                    toDate: to,
                    includesUnposted: unposted,
                });

            setIsLoading(false)
            //console.log(res.data.data.data.length)
            let totalDr = 0, totalCr = 0
            for (let i = 0; i < res.data.data.data.length; i++) {
                totalDr += res.data.data.data[i].dr
                totalCr += res.data.data.data[i].cr
            }
            setTotals([totalDr, totalCr])
            setData(res.data.data)
        } catch (error) {
            utils.showError(error)
        }
    }

    let pageMaxWidth = currentEnv.multiCurrency ? "1800px" : "1450px"
    let pageTitle = t('activity_report')
    let list = <div className="Print-Align-C Print-90-Percent" style={{maxWidth: pageMaxWidth}}>
        <div className="Spacing-V-16" />
        <div className="No-Screen">
            <div className="Row" style={{maxWidth: pageMaxWidth}}>
                <div className="Col Align-C" style={{fontSize: "21px", fontWeight: 700}}>{currentEnv.companyName}</div>
            </div>
            <div className="Spacing-V-8" />
            {currentEnv.companyAddress && currentEnv.companyAddress.split('\n').map((line) =>
                <div className="Row" style={{maxWidth: pageMaxWidth}}>
                    <div className="Col Align-C">{line}</div>
                </div>
            )}
        </div>
        <div className="Spacing-V-8" />
        <div className="Row" style={{maxWidth: pageMaxWidth}}>
            <div className="Col Align-C" style={{fontSize: "21px", fontWeight: 700}}>{pageTitle}</div>
        </div>
        {isLoading && <Spinner></Spinner>}
        <div className="Spacing-V-8" />
        {/*
        <div className="Row" style={{maxWidth: pageMaxWidth}}>
            <div className="Col Align-C" style={{fontSize: "14px", fontWeight: 600}}>Account From: {data.fromAcCode === "" ? "Any" : data.fromAcCode}<div className="Spacing-H-8"/>To: {data.toAcCode === "" ? "Any" : data.toAcCode}</div>
        </div>
        */}
        <div className="Row" style={{maxWidth: pageMaxWidth}}>
            <div className="Col Col-H Align-C" style={{fontSize: "14px", fontWeight: 600}}>{t('from')}: {data.fromDate}<div className="Spacing-H-8"/>{t('to')}: {data.toDate}</div>
        </div>
        {/* <div className="Spacing-V-16" />
        <HTMLTable striped interactive condensed>
            <thead>
                <tr>
                    <th className="TCol-Journal">{t('journal')}</th>
                    <th className="TCol-Voucher">{t('voucher')}</th>
                    <th className="TCol-Date">{t('date')}</th>
                    <th className="TCol-ACCode">{t('account')}</th>
                    <th className="TCol-ACName">{t('account_name')}</th>
                    <th className="TCol-Desc">{t('description')}</th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('dr')}</div></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('cr')}</div></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('balance')}</div></th>
                </tr>
            </thead>
            <tbody>
                {data.data && data.data.map((item: GlListItem) => <>
                    <tr>
                        <td className="TCol-Journal"></td>
                        <td className="TCol-Voucher"></td>
                        <td className="TCol-Date"></td>
                        <td className="TCol-ACCode"><b>{item.acCode}</b></td>
                        <td className="TCol-ACName"><b>{item.acName1}</b></td>
                        <td className="TCol-Desc"><b>{t('beginning_bal')}</b></td>
                        <td className="TCol-ACBalance"><div className="Align-R"></div></td>
                        <td className="TCol-ACBalance"><div className="Align-R"></div></td>
                        <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(item.bf)}</b></div></td>
                    </tr>
                    {item.journalEntries?.map((journal: VoucherItem) =>
                    <tr>
                        <td className="TCol-Journal">{journal.journalCode}</td>
                        <td className="TCol-Voucher"><a target="_blank" href={`#/${utils.journalBase(journal.type)}/detail/${journal.journalCode}/${journal.voucherCode}`}>{journal.voucherCode}</a></td>
                        <td className="TCol-Date">{journal.txDate}</td>
                        <td className="TCol-ACCode"></td>
                        <td className="TCol-ACName"></td>
                        <td className="TCol-Desc">{journal.description1}</td>
                        <td className="TCol-ACBalance"><div className="Align-R">{journal.dr > 0 && utils.formatMoney(journal.dr)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{journal.cr > 0 && utils.formatMoney(journal.cr)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{utils.formatMoney(journal.running)}</div></td>
                    </tr>
                    )}
                    <tr>
                        <td className="TCol-Journal"></td>
                        <td className="TCol-Voucher"></td>
                        <td className="TCol-Date"></td>
                        <td className="TCol-ACCode"></td>
                        <td className="TCol-ACName"></td>
                        <td className="TCol-Desc"><b>{t('account_total')}</b></td>
                        <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(item.dr)}</b></div></td>
                        <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(item.cr)}</b></div></td>
                        <td className="TCol-ACBalance"><div className="Align-R"></div></td>
                    </tr>
                    </>
                )}
            </tbody>
            <tfoot>
                <tr>
                    <td className="TCol-Journal"></td>
                    <td className="TCol-Voucher"></td>
                    <td className="TCol-Date"></td>
                    <td className="TCol-ACCode"></td>
                    <td className="TCol-ACName"></td>
                    <td className="TCol-Desc"><b>{t('TOTAL_TRANS')}</b></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(totals[0])}</b></div></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(totals[1])}</b></div></td>
                    <td className="TCol-ACBalance"><div className="Align-R"></div></td>
                </tr>
            </tfoot>
        </HTMLTable> */}
        <div className="Spacing-V-16" />
        <HTMLTable striped interactive condensed>
            <thead>
                {currentEnv.multiCurrency && <tr>
                    <th></th>
                    <th></th>
                    <th></th>
                    <th></th>
                    <th></th>
                    <th></th>
                    <th colSpan={2}><div className="Align-C">{t('in_base_currency')}</div></th>
                    <th className="THead-Sep"></th>
                    <th colSpan={3}><div className="Align-C">{t('in_foreign_currency')}</div></th>
                    <th></th>
                </tr>}
                <tr>
                    <th className="TCol-Journal">{t('journal')}</th>
                    <th className="TCol-Voucher">{t('voucher')}</th>
                    <th className="TCol-Date">{t('date')}</th>
                    <th className="TCol-ACCode">{t('account')}</th>
                    <th className="TCol-ACName">{t('account_name')}</th>
                    <th className="TCol-Desc">{t('description')}</th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('dr')}</div></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('cr')}</div></th>
                    {!currentEnv.multiCurrency && <th className="TCol-ACBalance"><div className="Align-R">{t('balance')}</div></th>}
                    {currentEnv.multiCurrency && <>
                    <th className="THead-Sep"></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('dr')}</div></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('cr')}</div></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('balance')}</div></th>
                    <th className="TCol-Currency">{t('currency')}</th>
                    </>}
                </tr>
            </thead>
            <tbody>
                {data.data && data.data.map((item: GlListItem) =>
                    Object.entries(item.journals).map((journal, i) => <>
                        <tr>
                            <td className="TCol-Journal"></td>
                            <td className="TCol-Voucher"></td>
                            <td className="TCol-Date"></td>
                            <td className="TCol-ACCode"><b>{item.acCode}</b></td>
                            <td className="TCol-ACName"><b>{item.acName1}</b></td>
                            <td className="TCol-Desc"><b>{t('beginning_bal')}</b></td>
                            <td className="TCol-ACBalance"><div className="Align-R"></div></td>
                            <td className="TCol-ACBalance"><div className="Align-R"></div></td>
                            {!currentEnv.multiCurrency && <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(journal[1].bf)}</b></div></td>}
                            {currentEnv.multiCurrency && <>
                            <td className="TCol-Sep"></td>
                            <td className="TCol-ACBalance"><div className="Align-R"></div></td>
                            <td className="TCol-ACBalance"><div className="Align-R"></div></td>
                            <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(journal[1].fbf)}</b></div></td>
                            <td className="TCol-Currency"><div className="Align-R"><b>{journal[0] === "" ? currentEnv.baseCurrency : journal[0]}</b></div></td>
                            </>}
                        </tr>
                        {journal[1].entries?.map((journalItem: VoucherItem) =>
                            <tr>
                                <td className="TCol-Journal">{journalItem.journalCode}</td>
                                <td className="TCol-Voucher"><a target="_blank" href={`#/${utils.journalBase(journalItem.type)}/detail/${journalItem.journalCode}/${journalItem.voucherCode}`}>{journalItem.voucherCode}</a></td>
                                <td className="TCol-Date">{journalItem.txDate}</td>
                                <td className="TCol-ACCode"></td>
                                <td className="TCol-ACName"></td>
                                <td className="TCol-Desc">{journalItem.description1}</td>
                                <td className="TCol-ACBalance"><div className="Align-R">{journalItem.dr > 0 && utils.formatMoney(journalItem.dr)}</div></td>
                                <td className="TCol-ACBalance"><div className="Align-R">{journalItem.cr > 0 && utils.formatMoney(journalItem.cr)}</div></td>
                                {!currentEnv.multiCurrency && <td className="TCol-ACBalance"><div className="Align-R">{utils.formatMoney(journalItem.running)}</div></td>}
                                {currentEnv.multiCurrency && <>
                                <td className="TCol-Sep"></td>
                                <td className="TCol-ACBalance"><div className="Align-R">{journalItem.fdr > 0 && utils.formatMoney(journalItem.fdr)}</div></td>
                                <td className="TCol-ACBalance"><div className="Align-R">{journalItem.fcr > 0 && utils.formatMoney(journalItem.fcr)}</div></td>
                                <td className="TCol-ACBalance"><div className="Align-R">{utils.formatMoney(journalItem.frunning)}</div></td>
                                <td className="TCol-Currency"><div className="Align-R"><b>{journal[0] === "" ? currentEnv.baseCurrency : journal[0]}</b></div></td>
                                </>}
                            </tr>
                        )}
                        <tr>
                            <td className="TCol-Journal"></td>
                            <td className="TCol-Voucher"></td>
                            <td className="TCol-Date"></td>
                            <td className="TCol-ACCode"></td>
                            <td className="TCol-ACName"></td>
                            <td className="TCol-Desc"><b>{t('account_total')}</b></td>
                            <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(journal[1].dr)}</b></div></td>
                            <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(journal[1].cr)}</b></div></td>
                            {!currentEnv.multiCurrency && <td className="TCol-ACBalance"><div className="Align-R"></div></td>}
                            {currentEnv.multiCurrency && <>
                            <td className="TCol-Sep"></td>
                            <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(journal[1].fdr)}</b></div></td>
                            <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(journal[1].fcr)}</b></div></td>
                            <td className="TCol-ACBalance"><div className="Align-R"></div></td>
                            <td className="TCol-Currency"><div className="Align-R"><b>{journal[0] === "" ? currentEnv.baseCurrency : journal[0]}</b></div></td>
                            </>}
                        </tr>
                    </>)
                )}
            </tbody>
            {/* <tfoot>
                <tr>
                    <td className="TCol-Journal"></td>
                    <td className="TCol-Voucher"></td>
                    <td className="TCol-Date"></td>
                    <td className="TCol-ACCode"></td>
                    <td className="TCol-ACName"></td>
                    <td className="TCol-Desc"><b>{t('TOTAL_TRANS')}</b></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(totals[0])}</b></div></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(totals[1])}</b></div></td>
                    {!currentEnv.multiCurrency && <td className="TCol-ACBalance"><div className="Align-R"></div></td>}
                </tr>
            </tfoot> */}
        </HTMLTable>
    </div>

    return (
        <>
            <form className="No-Print" onSubmit={onSubmit}>
                <div className="Row">
                    <FormGroup className="Col-1-1" label={t('from')} labelFor="fromAcCode">
                        {/*<InputGroup id="fromAcCode" value={fromAcCode} onChange={utils.handleStringChange(s => setFromAcCode(s))} />*/}
                        <Suggest
                                inputProps={{ placeholder: t('account'), onKeyDown: (e) => {e.key === "Enter" && e.preventDefault()} }}
                                defaultSelectedItem={acList.find((ac) => ac.acCode === fromAcCode)}
                                items={acList}
                                itemRenderer={acRenderer}
                                itemPredicate={acPredicate}
                                inputValueRenderer={acValueRenderer}
                                resetOnClose={true}
                                onItemSelect={(ac) => {
                                    setFromAcCode(ac.acCode)
                                }}
                            />
                    </FormGroup>
                    <FormGroup className="Col-2-1" label={t('to')} labelFor="toAcCode">
                        {/*<InputGroup id="toAcCode" value={toAcCode} onChange={utils.handleStringChange(s => setToAcCode(s))} />*/}
                        <Suggest
                                inputProps={{ placeholder: t('account'), onKeyDown: (e) => {e.key === "Enter" && e.preventDefault()} }}
                                defaultSelectedItem={acList.find((ac) => ac.acCode === toAcCode)}
                                items={acList}
                                itemRenderer={acRenderer}
                                itemPredicate={acPredicate}
                                inputValueRenderer={acValueRenderer}
                                resetOnClose={true}
                                onItemSelect={(ac) => {
                                    setToAcCode(ac.acCode)
                                }}
                            />
                    </FormGroup>
                    <FormGroup className="Col-3-1" label={t('from')} labelFor="from">
                        <InputGroup id="from" value={from} type="date" onChange={utils.handleStringChange(s => setFrom(s))} />
                    </FormGroup>
                    <FormGroup className="Col-4-1" label={t('to')} labelFor="to">
                        <InputGroup id="to" value={to} type="date" onChange={utils.handleStringChange(s => setTo(s))} />
                    </FormGroup>
                    <Button className="Col-5-1 Button-LabeledFormGroup" icon="desktop" type="submit" >{t('preview')}</Button>
                    <AnchorButton className="Col-6-1 Button-LabeledFormGroup" icon="download" target="_blank" href={`${svc.defaults.baseURL}/downloadactvtyrpt?fromAC=${fromAcCode}&toAC=${toAcCode}&from=${from}&to=${to}`}>Excel</AnchorButton>
                    {/*<AnchorButton className="Col-4-1 Button-LabeledFormGroup" icon="document" target="_blank" href={`${svc.defaults.baseURL}/downloadtb?from=${from}&to=${to}&inline`+ (unposted ? "&unposted" : "")+ (general ? "&general" : "")+ (zero ? "&zero" : "")}>PDF</AnchorButton>*/}
                    {/*<AnchorButton className="Col-5-1 Button-LabeledFormGroup" icon="document" target="_blank" href={`${svc.defaults.baseURL}/downloadtb?from=${from}&to=${to}`+ (unposted ? "&unposted" : "")+ (general ? "&general" : "")+ (zero ? "&zero" : "")}>Download PDF</AnchorButton>*/}
                </div>
                {/*<div className="Row">
                    <Checkbox className="Col-1-1" label={t('includes_unposted')} checked={unposted} onChange={utils.handleBooleanChange(v => setUnposted(v))} />
                </div>*/}
            </form>
            {list}
        </>
    );
}

export default ActivityReport